<script setup>
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionRoot,
} from "@headlessui/vue";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  ListBulletIcon,
  NumberedListIcon,
  ChevronDownIcon,
  Bars3Icon,
  Bars3BottomRightIcon,
  Bars3BottomLeftIcon,
  Bars4Icon,
  CodeBracketIcon,
  PencilSquareIcon,
  LinkIcon,
  ChevronUpDownIcon,
} from "@heroicons/vue/24/outline";
import ActionButton from "@js/components/ActionButton.vue";
import Modal from "@js/components/Modal.vue";
import InputField from "@js/components/fields/InputField.vue";
import SwitchField from "@js/components/fields/SwitchField.vue";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import { Color } from "@tiptap/extension-color";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Strike from "@tiptap/extension-strike";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import { ErrorMessage, Field } from "vee-validate";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const {
  fieldId,
  fieldName,
  required = false,
  label,
  placeholder = "",
  disabled = false,
} = defineProps({
  fieldId: {
    type: String,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    required: false,
  },
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
});

const { t } = useI18n();

const model = defineModel();

const editor = useEditor({
  content: model.value,
  extensions: [
    Document,
    Paragraph,
    Placeholder.configure({
      placeholder,
      emptyNodeClass: "my-custom-is-empty-class",
    }),
    Typography,
    Text,
    Heading,
    Bold,
    Italic,
    Underline,
    Strike,
    ListItem,
    BulletList,
    OrderedList,
    HardBreak,
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    Link.configure({
      openOnClick: false,
      target: null,
      rel: null,
    }),
    TextStyle,
    Highlight.configure({ multicolor: true }),
    Color.configure({
      types: ["textStyle"],
    }),
    History,
  ],
  onUpdate: ({ editor }) => {
    model.value = editor.getHTML();
  },
  editorProps: {
    transformPastedText(text) {
      text = text.replace(/’/g, "'");
      text = text.replace(/—/g, "-");

      return text;
    },
    transformPastedHTML(html) {
      html = html.replace(/’/g, "'");
      html = html.replace(/—/g, "-");

      return html;
    },
  },
  editable: !disabled,
});

const color = ref("#000000");

const linkModalIsOpen = ref(false);

const linkUrl = ref("");

const linkTargetBlank = ref(true);

const showHtml = ref(false);

const colorIsDark = computed(() => {
  let c = color.value.substring(1);
  let rgb = parseInt(c, 16);
  let r = (rgb >> 16) & 0xff;
  let g = (rgb >> 8) & 0xff;
  let b = (rgb >> 0) & 0xff;

  let luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luma < 40;
});

function setLinkModalIsOpen(value) {
  if (value && editor.value.isActive("link")) {
    linkUrl.value = editor.value.getAttributes("link").href;
    linkTargetBlank.value =
      editor.value.getAttributes("link").target === "_blank";
  } else {
    linkUrl.value = "";
    linkTargetBlank.value = true;
  }

  linkModalIsOpen.value = value;
}

function createLink() {
  editor.value
    .chain()
    .focus()
    .extendMarkRange("link")
    .setLink({
      href: linkUrl.value,
      target: linkTargetBlank.value ? "_blank" : null,
      rel: null,
    })
    .run();

  linkUrl.value = "";
  linkTargetBlank.value = true;

  setLinkModalIsOpen(false);
}

function deleteLink() {
  editor.value.chain().focus().extendMarkRange("link").unsetLink().run();

  setLinkModalIsOpen(false);
}

function toggleShowHtml() {
  if (showHtml.value) {
    editor.value.commands.setContent(model.value);
  }

  showHtml.value = !showHtml.value;
}

watch(model, () => {
  if (model.value === "<p></p>") {
    value.value = "";
  }
});
</script>

<template>
  <div class="mb-4 flex flex-col">
    <label :for="fieldName" class="mb-2">
      {{ label }}
      <sup v-if="required" class="text-red-500">*</sup>
    </label>

    <Field v-model="model" :id="fieldId" :name="fieldName">
      <div
        v-if="editor"
        class="w-full"
        :class="{ 'cursor-not-allowed opacity-40': disabled }"
      >
        <div
          class="relative z-10 -mb-px flex w-full flex-wrap gap-4 rounded-t border border-gray-300 bg-white px-5 py-3"
        >
          <Menu
            v-if="!showHtml"
            v-slot="{ open }"
            as="div"
            class="relative z-10 flex flex-col items-start"
          >
            <MenuButton
              type="button"
              class="inline-flex size-8 w-44 cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-1 font-bold duration-150 focus:bg-gray-200 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{ 'bg-gray-200': open, 'bg-white': !open }"
              :disabled="!editor.isEditable"
            >
              <template v-if="editor.isActive('heading', { level: 1 })">
                {{ t("label.heading", { level: 1 }) }}
              </template>
              <template v-else-if="editor.isActive('heading', { level: 2 })">
                {{ t("label.heading", { level: 2 }) }}
              </template>
              <template v-else-if="editor.isActive('heading', { level: 3 })">
                {{ t("label.heading", { level: 3 }) }}
              </template>
              <template v-else-if="editor.isActive('heading', { level: 4 })">
                {{ t("label.heading", { level: 4 }) }}
              </template>
              <template v-else-if="editor.isActive('heading', { level: 5 })">
                {{ t("label.heading", { level: 5 }) }}
              </template>
              <template v-else-if="editor.isActive('heading', { level: 6 })">
                {{ t("label.heading", { level: 6 }) }}
              </template>
              <template v-else-if="editor.isActive('paragraph')">
                {{ t("label.paragraph") }}
              </template>
              <template v-else> {{ t("label.paragraph") }}</template>

              <ChevronDownIcon
                class="size-4 flex-shrink-0 duration-150"
                :class="{ 'rotate-180': open }"
              />
            </MenuButton>

            <TransitionRoot
              :as="MenuItems"
              :show="open"
              class="absolute left-0 top-10 flex w-full flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow duration-150 focus:outline-0"
              enter-from="opacity-0 translate-y-2"
              enter-to="opacity-100 translate-y-0"
              leave-from="opacity-100 translate-y-0"
              leave-to="opacity-0 translate-y-2"
            >
              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive('heading', { level: 1 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  "
                >
                  {{ t("label.heading", { level: 1 }) }}
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive('heading', { level: 2 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  "
                >
                  {{ t("label.heading", { level: 2 }) }}
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive('heading', { level: 3 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  "
                >
                  {{ t("label.heading", { level: 3 }) }}
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive('heading', { level: 4 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 4 }).run()
                  "
                >
                  {{ t("label.heading", { level: 4 }) }}
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive('heading', { level: 5 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 5 }).run()
                  "
                >
                  {{ t("label.heading", { level: 5 }) }}
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive('heading', { level: 6 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 6 }).run()
                  "
                >
                  {{ t("label.heading", { level: 6 }) }}
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200': active || editor.isActive('paragraph'),
                  }"
                  @click="editor.commands.setParagraph()"
                >
                  {{ t("label.paragraph") }}
                </button>
              </MenuItem>
            </TransitionRoot>
          </Menu>

          <Menu
            v-if="!showHtml"
            v-slot="{ open }"
            as="div"
            class="relative z-10 flex flex-col items-start"
          >
            <MenuButton
              type="button"
              class="inline-flex size-8 w-16 cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-1 font-bold duration-150 focus:bg-gray-200 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{ 'bg-gray-200': open, 'bg-white': !open }"
              :disabled="!editor.isEditable"
            >
              <template v-if="editor.isActive({ textAlign: 'left' })">
                <Bars3BottomLeftIcon class="size-4" />
              </template>
              <template v-else-if="editor.isActive({ textAlign: 'center' })">
                <Bars3Icon class="size-4" />
              </template>
              <template v-else-if="editor.isActive({ textAlign: 'right' })">
                <Bars3BottomRightIcon class="size-4" />
              </template>
              <template v-else-if="editor.isActive({ textAlign: 'justify' })">
                <Bars4Icon class="size-4" />
              </template>
              <template v-else>
                <Bars3BottomLeftIcon class="size-4" />
              </template>

              <ChevronDownIcon
                class="size-4 flex-shrink-0 duration-150"
                :class="{ 'rotate-180': open }"
              />
            </MenuButton>

            <TransitionRoot
              :as="MenuItems"
              :show="open"
              class="absolute left-0 top-10 flex w-full flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow duration-150 focus:outline-0"
              enter-from="opacity-0 translate-y-2"
              enter-to="opacity-100 translate-y-0"
              leave-from="opacity-100 translate-y-0"
              leave-to="opacity-0 translate-y-2"
            >
              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive({ textAlign: 'left' }),
                  }"
                  @click="editor.chain().focus().setTextAlign('left').run()"
                >
                  <Bars3BottomLeftIcon class="size-4" />
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive({ textAlign: 'center' }),
                  }"
                  @click="editor.chain().focus().setTextAlign('center').run()"
                >
                  <Bars3Icon class="size-4" />
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive({ textAlign: 'right' }),
                  }"
                  @click="editor.chain().focus().setTextAlign('right').run()"
                >
                  <Bars3BottomRightIcon class="size-4" />
                </button>
              </MenuItem>

              <MenuItem v-slot="{ active }" as="template">
                <button
                  type="button"
                  class="w-full cursor-pointer whitespace-nowrap px-3 py-1 text-left text-sm font-normal duration-150 hover:bg-gray-200"
                  :class="{
                    'bg-gray-200':
                      active || editor.isActive({ textAlign: 'justify' }),
                  }"
                  @click="editor.chain().focus().setTextAlign('justify').run()"
                >
                  <Bars4Icon class="size-4" />
                </button>
              </MenuItem>
            </TransitionRoot>
          </Menu>

          <div v-if="!showHtml" class="flex gap-2">
            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('bold'),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <BoldIcon class="size-4" />
            </button>

            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('italic'),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <ItalicIcon class="size-4" />
            </button>

            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('underline'),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <UnderlineIcon class="size-4" />
            </button>

            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('strike'),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <StrikethroughIcon class="size-4" />
            </button>
          </div>

          <div v-if="!showHtml" class="flex gap-2">
            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('bulletList'),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleBulletList().run()"
            >
              <ListBulletIcon class="size-4" />
            </button>

            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('orderedList'),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleOrderedList().run()"
            >
              <NumberedListIcon class="size-4" />
            </button>
          </div>

          <div v-if="!showHtml" class="flex gap-2">
            <div class="relative">
              <input
                v-model="color"
                type="color"
                class="h-8 cursor-pointer appearance-none rounded-md border border-gray-200 bg-transparent duration-150 hover:bg-gray-200"
                list="presetColors"
              />
              <datalist id="presetColors">
                <option value="#cccccc" />
                <option value="#ffff00" />
                <option value="#00ff00" />
                <option value="#00ffff" />
                <option value="#ff00ff" />
                <option value="#ff9900" />
                <option value="#ff0000" />
              </datalist>
              <ChevronUpDownIcon
                class="absolute right-1 top-1/2 size-4 -translate-y-1/2"
              />
            </div>

            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('textStyle', { color }),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().setColor(color).run()"
            >
              <span
                class="inline-block size-4 leading-4 underline decoration-2"
                :style="'text-decoration-color: ' + color"
              >
                A
              </span>
            </button>

            <button
              type="button"
              class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
              :class="{
                '!bg-gray-200': editor.isActive('highlight', { color }),
              }"
              :disabled="!editor.isEditable"
              @click="editor.chain().focus().toggleHighlight({ color }).run()"
            >
              <span
                class="inline-block size-4 leading-4"
                :class="{
                  'text-white': colorIsDark,
                  'text-black': !colorIsDark,
                }"
                :style="'background-color: ' + color"
              >
                A
              </span>
            </button>
          </div>

          <button
            v-if="!showHtml"
            type="button"
            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
            :disabled="!editor.isEditable"
            :class="{
              '!bg-gray-200': editor.isActive('link'),
            }"
            @click="setLinkModalIsOpen(true)"
          >
            <LinkIcon class="size-4" />
          </button>

          <button
            type="button"
            class="size-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 duration-150 focus:bg-gray-100 focus:outline-0 enabled:hover:bg-gray-200 disabled:cursor-not-allowed"
            :disabled="!editor.isEditable"
            @click="toggleShowHtml"
          >
            <CodeBracketIcon v-if="!showHtml" class="size-4" />
            <PencilSquareIcon v-else class="size-4" />
          </button>
        </div>

        <EditorContent
          :editor="editor"
          class="wysiwyg w-full overflow-auto rounded-b border border-gray-300 px-5 py-3 ring-1 ring-transparent duration-150 has-[.ProseMirror-focused]:ring-gray-300"
          :class="{ hidden: showHtml }"
        />
        <textarea
          v-model="model"
          class="h-[40rem] w-full overflow-auto rounded-b border border-gray-300 px-5 py-3 ring-1 ring-transparent duration-150 focus:outline-0 focus:ring-gray-300"
          :class="{ hidden: !showHtml }"
        ></textarea>
      </div>
    </Field>

    <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
  </div>

  <Modal
    :is-open="linkModalIsOpen"
    :set-is-open="setLinkModalIsOpen"
    size="small"
  >
    <template #title>
      {{ t("title.editLink") }}
    </template>

    <template #full-content>
      <div class="h-full max-h-[calc(100vh-12rem)] overflow-y-auto p-6">
        <InputField
          v-model="linkUrl"
          :label="t('label.linkUrl')"
          field-name="linkUrl"
          field-id="linkUrl"
          required
        />

        <SwitchField
          v-model="linkTargetBlank"
          :label="t('label.linkTargetBlank')"
          field-name="linkTargetBlank"
          field-id="linkTargetBlank"
        />
      </div>

      <div
        class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 p-6"
      >
        <ActionButton
          v-if="editor.getAttributes('link').href"
          :callback="deleteLink"
        >
          {{ t("button.deleteLink") }}
        </ActionButton>

        <ActionButton :callback="createLink">
          {{ t("button.createLink") }}
        </ActionButton>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
:deep(.tiptap) {
  height: 40rem;
  outline: 0;
}

:deep(.tiptap) p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #b0b0b0;
  pointer-events: none;
  height: 0;
}
</style>
